import z from "zod";

export const GIT_BRANCH_NAME_MAX_LENGTH = 128;

const FORBIDDEN_GIT_REF_CHARACTERS = new Set([
  " ",
  "~",
  "^",
  ":",
  "?",
  "*",
  "[",
  "\\",
]);

export function isValidGitBranchName(value: string): boolean {
  const branchName = value.trim();
  if (
    branchName.length === 0 ||
    branchName.length > GIT_BRANCH_NAME_MAX_LENGTH ||
    branchName.startsWith("-") ||
    branchName.startsWith("/") ||
    branchName.endsWith("/") ||
    branchName.endsWith(".") ||
    branchName === "HEAD" ||
    branchName.includes("..") ||
    branchName.includes("@{")
  ) {
    return false;
  }
  if (hasForbiddenGitRefCharacter(branchName)) {
    return false;
  }
  return branchName
    .split("/")
    .every(
      (component) =>
        component.length > 0 &&
        !component.startsWith(".") &&
        !component.endsWith(".lock"),
    );
}

function hasForbiddenGitRefCharacter(value: string): boolean {
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (
      codePoint === undefined ||
      codePoint < 0x20 ||
      codePoint === 0x7f ||
      FORBIDDEN_GIT_REF_CHARACTERS.has(character)
    ) {
      return true;
    }
  }
  return false;
}

export const gitBranchNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(GIT_BRANCH_NAME_MAX_LENGTH)
  .refine(isValidGitBranchName, { message: "Invalid Git branch name." });

/**
 * Names Arti CREATES on a remote are restricted to ASCII
 * `[A-Za-z0-9._/-]` on top of the general Git rules, so the requested name,
 * the provider ref, the provision ledger (VARCHAR) and the attachment
 * `default_branch` (VARCHAR(128)) agree byte-for-byte. Existing branches keep
 * the broader `gitBranchNameSchema`.
 */
export function isCreatableGitBranchName(value: string): boolean {
  return /^[A-Za-z0-9._/-]+$/u.test(value) && isValidGitBranchName(value);
}

export const creatableGitBranchNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(GIT_BRANCH_NAME_MAX_LENGTH)
  .refine(isCreatableGitBranchName, {
    message:
      "Branch names Arti creates must use ASCII letters, digits, '.', '_', '-' and '/' only.",
  });
